<template>
  <div class="lifecycle-emissions" :style="rootStyle">
    <div v-if="showToolbar" class="lifecycle-toolbar">
      <div class="lifecycle-toolbar-label">
        <span v-if="showSectionRequired" class="required">*</span>
        <span class="label-line">生命周期各阶段碳排放</span>
        <span class="label-line">（每功能单位下）:</span>
      </div>
      <div class="lifecycle-toolbar-controls">
        <el-select
          v-if="linkedUnitMode || !hideUnitSelector"
          class="lifecycle-unit-select"
          :value="lifecycleCarbonUnitModel"
          :disabled="readonly"
          placeholder="请选择"
          clearable
          @change="onLifecycleCarbonUnitSelect"
        >
          <el-option v-for="opt in unitOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
        </el-select>
        <button v-if="!readonly && hasUnitSelected" type="button" class="lifecycle-detail-toggle" @click="toggleDetailsExpanded">
          <span v-if="showSectionRequired" class="required">*</span>
          <span>详细参数</span>
          <i :class="detailsExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
        </button>
      </div>
    </div>

    <div v-show="detailsExpanded && hasUnitSelected" class="lifecycle-detail-box lifecycle-detail-box--aligned">
      <div class="emissions-panel">
        <div class="emissions-grid">
          <div class="emissions-column">
            <template v-for="(item, index) in emissionsData.lifeCarbonList">
              <div v-if="item.stageType % 2 !== 0" :key="`odd-${index}`" class="emission-stage">
                <div class="stage-label">
                  <span v-if="isStageRequired(item.stageType)" class="required">*</span>
                  <span class="stage-label-text" v-html="info[item.stageType] && info[item.stageType].title"></span>
                </div>
                <div class="stage-inputs">
                  <div class="input-group">
                    <span v-if="isStageRequired(item.stageType)" class="require-label">*</span>
                    <label>碳排放量</label>
                    <el-input
                      v-model="item.carbonEmission"
                      :disabled="readonly"
                      class="stage-carbon-input"
                      :class="[
                        info[item.stageType] && info[item.stageType].Name ? info[item.stageType].Name : '',
                        { 'is-field-error': isCarbonInputError(item) },
                      ]"
                      maxlength="100"
                      placeholder="请输入"
                      @input="(val) => handleInput(val, index)"
                    />
                  </div>
                  <div class="input-group">
                    <span v-if="isStageRequired(item.stageType)" class="require-label">*</span>
                    <label>占比</label>
                    <el-input
                      v-model="item.proportion"
                      :disabled="readonly"
                      class="stage-proportion-input"
                      :class="{ 'is-field-error': isProportionInputError(item) }"
                      maxlength="100"
                      placeholder="请输入"
                      @input="(val) => handleInputProportion(val, index)"
                    />
                    <i class="unit">%</i>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <div class="divider"></div>

          <div class="emissions-column">
            <template v-for="(item, index) in emissionsData.lifeCarbonList">
              <div v-if="item.stageType % 2 === 0" :key="`even-${index}`" class="emission-stage">
                <div class="stage-label">
                  <div class="stage-label-inner">
                    <span v-if="isStageRequired(item.stageType)" class="required">*</span>
                    <span class="stage-label-text" v-html="info[item.stageType] && info[item.stageType].title"></span>
                  </div>
                </div>
                <div class="stage-inputs">
                  <div class="input-group">
                    <span v-if="isStageRequired(item.stageType)" class="require-label">*</span>
                    <label>碳排放量</label>
                    <el-input
                      v-model="item.carbonEmission"
                      :disabled="readonly"
                      class="stage-carbon-input"
                      :class="[
                        info[item.stageType] && info[item.stageType].Name ? info[item.stageType].Name : '',
                        { 'is-field-error': isCarbonInputError(item) },
                      ]"
                      maxlength="100"
                      placeholder="请输入"
                      @input="(val) => handleInput(val, index)"
                    />
                  </div>
                  <div class="input-group">
                    <span v-if="isStageRequired(item.stageType)" class="require-label">*</span>
                    <label>占比</label>
                    <el-input
                      v-model="item.proportion"
                      :disabled="readonly"
                      class="stage-proportion-input"
                      :class="{ 'is-field-error': isProportionInputError(item) }"
                      maxlength="100"
                      placeholder="请输入"
                      @input="(val) => handleInputProportion(val, index)"
                    />
                    <i class="unit">%</i>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { isValidLifecycleCarbonUnit, CARBON_UNIT_OPTIONS } from '../utils/carbonUnits';

const info = {
  1: { title: '原材料获取阶段<br/>（上游生产）', require: true },
  2: { title: '原材料获取阶段<br/>（运输）', require: true, Name: 'require1' },
  3: { title: '生产阶段', require: true, Name: 'require2' },
  4: { title: '运输（交付）阶段', require: true, Name: 'require3' },
  5: { title: '使用阶段', require: true },
  6: { title: '生命末期阶段', require: true },
};

const defaultLifeCarbonList = () => [
  { stageType: 1, carbonEmission: '', proportion: '' },
  { stageType: 2, carbonEmission: '', proportion: '' },
  { stageType: 3, carbonEmission: '', proportion: '' },
  { stageType: 4, carbonEmission: '', proportion: '' },
  { stageType: 5, carbonEmission: '', proportion: '' },
  { stageType: 6, carbonEmission: '', proportion: '' },
];

export default {
  name: 'LifecycleEmissions',
  props: {
    showToolbar: { type: Boolean, default: true },
    optionalMode: { type: Boolean, default: false },
    /** 为 true 时隐藏单位选择，沿用父级每功能单位碳足迹的单位 */
    hideUnitSelector: { type: Boolean, default: false },
    /** 为 true 时展示与上方联动的只读单位下拉 */
    linkedUnitMode: { type: Boolean, default: false },
    emissions: {
      type: Object,
      default: () => ({
        lifeCarbonList: defaultLifeCarbonList(),
      }),
    },
    formLabelWidth: { type: [Number, String], default: 160 },
    readonly: { type: Boolean, default: false },
    /** 校验失败时，未填写的必填碳排放量输入框显示红框 */
    showEmptyFieldErrors: { type: Boolean, default: false },
  },
  data() {
    return {
      info,
      unitOptions: CARBON_UNIT_OPTIONS,
      detailsExpanded: false,
      suppressEmit: false,
      emissionsData: {
        lifeCarbonList: defaultLifeCarbonList(),
      },
    };
  },
  computed: {
    labelWidthCss() {
      const w = this.formLabelWidth;
      return typeof w === 'number' ? `${w}px` : String(w);
    },
    rootStyle() {
      return { '--lf-label-width': '160px' /* this.labelWidthCss */ };
    },
    lifecycleCarbonUnitModel() {
      const e = this.emissions || {};
      const u = this.linkedUnitMode || this.hideUnitSelector ? e.carbonUnit : e.lifecycleCarbonUnit;
      if (u === '' || u === null || u === undefined) {
        return undefined;
      }
      const n = Number(u);
      return isValidLifecycleCarbonUnit(n) ? n : undefined;
    },
    hasUnitSelected() {
      return isValidLifecycleCarbonUnit(this.lifecycleCarbonUnitModel);
    },
    hasLifecycleDetailData() {
      const list = this.emissionsData.lifeCarbonList || [];
      return list.some((item) => {
        const em = item.carbonEmission != null && String(item.carbonEmission).trim() !== '';
        const pr = item.proportion != null && String(item.proportion).trim() !== '';
        return em || pr;
      });
    },
    showSectionRequired() {
      return !this.optionalMode;
    },
  },
  watch: {
    hasUnitSelected: {
      immediate: true,
      handler(val) {
        if (val && !this.optionalMode) {
          this.detailsExpanded = true;
        }
      },
    },
    emissionsData: {
      handler(newVal) {
        if (this.suppressEmit) {
          return;
        }
        this.$emit('input', {
          lifeCarbonList: newVal.lifeCarbonList,
        });
      },
      deep: true,
    },
  },
  mounted() {
    this.syncEmissionsDataFromProps();
    if (this.hasUnitSelected && this.hasLifecycleDetailData) {
      this.detailsExpanded = true;
    }
  },
  methods: {
    syncEmissionsDataFromProps() {
      const src = this.emissions || {};
      const list = src.lifeCarbonList;
      this.suppressEmit = true;
      this.emissionsData = {
        lifeCarbonList:
          list && Array.isArray(list) && list.length ? JSON.parse(JSON.stringify(list)) : defaultLifeCarbonList(),
      };
      this.$nextTick(() => {
        this.suppressEmit = false;
      });
    },
    toggleDetailsExpanded() {
      this.detailsExpanded = !this.detailsExpanded;
    },
    expandDetails() {
      this.detailsExpanded = true;
    },
    isStageRequired(stageType) {
      if (this.optionalMode) {
        return false;
      }
      return !!(info[stageType] && info[stageType].require);
    },
    isCarbonInputError(item) {
      if (!this.showEmptyFieldErrors || !this.isStageRequired(item.stageType)) {
        return false;
      }
      const em = item.carbonEmission != null ? String(item.carbonEmission).trim() : '';
      return !em;
    },
    isProportionInputError(item) {
      if (!this.showEmptyFieldErrors || !this.isStageRequired(item.stageType)) {
        return false;
      }
      const pr = item.proportion != null ? String(item.proportion).trim() : '';
      return !pr;
    },
    onLifecycleCarbonUnitSelect(val) {
      const n = val === null || val === undefined || val === '' ? NaN : Number(val);
      const normalized = isValidLifecycleCarbonUnit(n) ? n : null;
      if (!normalized) {
        this.detailsExpanded = false;
      }
      this.$emit('lifecycleCarbonUnitChange', normalized);
    },
    handleInput(val, index) {
      const clean = val
        .replace(/[^\d.]/g, '')
        .replace(/^(\d*\.\d*?)\..*$/, '$1')
        .replace(/^0+(\d)/, '$1');
      this.emissionsData.lifeCarbonList[index].carbonEmission = clean;
    },
    handleInputProportion(val, index) {
      const clean = val
        .replace(/[^\d.]/g, '')
        .replace(/^(\d*\.\d*?)\..*$/, '$1')
        .replace(/^0+(\d)/, '$1');

      if (clean === '.' || clean === '0.') {
        this.emissionsData.lifeCarbonList[index].proportion = clean;
        return;
      }

      if (clean > 100) {
        this.emissionsData.lifeCarbonList[index].proportion = '100';
        return;
      }

      this.emissionsData.lifeCarbonList[index].proportion = clean;
    },
  },
};
</script>

<style lang="scss" scoped>
$lifecycle-border: #d9d9d9;
$lifecycle-label-bg: #f5f5f5;
$lifecycle-label-width: 152px;

.lifecycle-emissions {
  width: 100%;
  box-sizing: border-box;
}

$lf-label-pad-right: 10px;

.lifecycle-toolbar {
  display: flex;
  align-items: flex-start;
  gap: 0;
  margin-bottom: 10px;
}

.lifecycle-toolbar-label {
  flex-shrink: 0;
  width: var(--lf-label-width, 160px);
  padding-right: $lf-label-pad-right;
  box-sizing: border-box;
  text-align: right;
  font-size: 14px;
  color: #666;
  line-height: 1.35;
  min-height: 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;

  > .required {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    line-height: 1;
  }
}

.label-line {
  display: block;
  white-space: nowrap;
}

.lifecycle-toolbar-controls {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 30px;
  padding-left: 0;
}

.lifecycle-unit-select {
  flex: 0 0 330px;
  width: 330px;
  max-width: 330px;
}

.lifecycle-detail-toggle {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: transparent;
  color: #2587ff;
  font-size: 14px;
  line-height: 30px;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    opacity: 0.85;
  }
  i {
    font-size: 12px;
    font-weight: bold;
  }
}

.lifecycle-detail-box--aligned {
  margin-left: var(--lf-label-width, 160px);
  width: calc(100% - var(--lf-label-width, 160px));
  box-sizing: border-box;
}

.lifecycle-detail-box {
  border: 1px solid $lifecycle-border;
  border-radius: 2px;
  overflow: hidden;
  background: #fff;
}

.emissions-panel {
  background: #fff;
}

.emissions-grid {
  display: flex;
  align-items: stretch;
}

.emissions-column {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.divider {
  flex: 0 0 1px;
  width: 1px;
  background: $lifecycle-border;
  align-self: stretch;
}

.emission-stage {
  min-height: 52px;
  display: flex;
  align-items: stretch;
  flex-wrap: nowrap;
  border-bottom: 1px solid $lifecycle-border;
  box-sizing: border-box;
}

.emissions-column .emission-stage:last-child {
  border-bottom: none;
}

.stage-label {
  flex-shrink: 0;
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  width: $lifecycle-label-width;
  min-height: 52px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  line-height: 1.35;
  border-right: 1px solid $lifecycle-border;
  background: $lifecycle-label-bg;
}

.stage-label-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 2px;
  max-width: 100%;
  text-align: center;
}

.stage-label-inner .required {
  flex-shrink: 0;
  line-height: 1.35;
}

.stage-label-text {
  display: block;
  max-width: 100%;
  text-align: center;
  white-space: normal;
  word-break: break-word;
}

.stage-inputs {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
  padding: 10px 14px;
  background: #fff;
}

.input-group {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  line-height: 30px;
  position: relative;
  white-space: nowrap;

  .require-label {
    position: absolute;
    color: #f0250f;
    left: -8px;
    top: 0;
  }

  label {
    flex-shrink: 0;
    font-size: 14px;
    color: #666;
    margin-right: 8px;
  }
}

.stage-carbon-input {
  width: 102px;
}

.stage-proportion-input {
  width: 80px;
}

.total-emission {
  display: flex;
  align-items: stretch;
  border-top: 1px solid $lifecycle-border;
  min-height: 52px;
}

.total-label {
  flex-shrink: 0;
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  width: $lifecycle-label-width;
  background: $lifecycle-label-bg;
  text-align: center;
  padding: 12px 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid $lifecycle-border;
  line-height: 1.35;
  white-space: nowrap;
}

.total-input {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  padding: 10px 14px;
  background: #fff;
}

.required {
  color: #f0250f;
}

.unit {
  color: #333;
  margin-left: 6px;
  font-size: 14px;
  font-style: normal;
}

::v-deep .lifecycle-unit-select {
  width: 330px;
}
::v-deep .lifecycle-unit-select .el-input__inner {
  height: 30px !important;
  line-height: 30px !important;
  border-radius: 2px;
}

::v-deep .lifecycle-unit-select .el-input.is-focus .el-input__inner,
::v-deep .lifecycle-unit-select .el-input__inner:focus {
  border-color: #2587ff;
}

::v-deep .stage-carbon-input .el-input__inner,
::v-deep .stage-proportion-input .el-input__inner,
::v-deep .total-input .el-input__inner {
  height: 30px !important;
  line-height: 30px !important;
  border-radius: 2px;
}

::v-deep .stage-carbon-input.is-field-error .el-input__inner {
  border-color: #f56c6c !important;
}

::v-deep .stage-proportion-input.is-field-error .el-input__inner {
  border-color: #f56c6c !important;
}
</style>
